import { FormHelperText, styled } from '@mui/material';
import React from 'react';
import { FlexBox } from '../box';
const escapeXml = (s) => s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
export const buildSvg = (text, width, height, fontSize, base64Font, watermark) => {
    const safeText = escapeXml(text);
    const safeWatermark = escapeXml(watermark || '');
    const fontFace = base64Font
        ? `
        @font-face {
                font-family: 'Pacifico';
                src: url('data:font/woff2;base64,${base64Font}') format('woff2');
                font-weight: normal;
                font-style: normal;
            }
    `
        : '';
    const watermarkSvg = safeWatermark
        ? `<text x="${width - 8}" y="10" text-anchor="end" font-family="sans-serif" dominant-baseline="hanging" font-size="12" opacity="0.5">${safeWatermark}</text>`
        : '';
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" preserveAspectRatio="xMidYMid meet">
                <style>${fontFace}</style>
                ${watermarkSvg}
                <text x="50%" y="50%" text-anchor="middle" dominant-baseline="central" font-family="Pacifico, cursive" font-size="${fontSize}" fill="black">${safeText}</text>
            </svg>`;
};
export const svgToDataUrl = (svg) => {
    const encoded = unescape(encodeURIComponent(svg));
    const base64 = window.btoa(encoded);
    return base64;
};
export const useBase64Font = (fontUrl) => {
    // eslint-disable-next-line import/no-internal-modules
    void import('@fontsource/pacifico/400.css');
    const [base64Font, setBase64Font] = React.useState(null);
    React.useEffect(() => {
        if (!fontUrl) {
            setBase64Font(null);
            return;
        }
        const controller = new AbortController();
        void (async () => {
            try {
                const res = await fetch(fontUrl, { signal: controller.signal });
                if (!res.ok) {
                    throw new Error(`Failed to fetch font: ${res.status}`);
                }
                const buffer = await res.arrayBuffer();
                const base64 = btoa(new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), ''));
                setBase64Font(base64);
            }
            catch (e) {
                if (process.env.NODE_ENV === 'development') {
                    console.warn(e);
                }
                if (!controller.signal.aborted) {
                    setBase64Font(null);
                }
            }
        })();
        return () => controller.abort();
    }, [fontUrl]);
    return base64Font;
};
export const Wrapper = styled(FlexBox)(({ theme }) => ({
    border: '1px solid',
    borderColor: theme.components?.input?.outlined?.['enabled-border'],
    borderRadius: theme.shape.borderRadius,
    position: 'relative',
    backgroundColor: theme.palette.background.paper
}));
export const HelperText = styled(FormHelperText)(({ theme, disabled, error }) => ({
    color: disabled
        ? theme.palette.text.disabled
        : error
            ? theme.palette.error.main
            : theme.palette.text.secondary
}));
let ID_COUNTER = 0;
export const getNextId = (prefix = 'element') => `${prefix}-${ID_COUNTER++}`;
const base64ToUtf8 = (b64) => {
    if (typeof window !== 'undefined' && typeof window.atob === 'function') {
        const binary = window.atob(b64);
        const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
        return new TextDecoder().decode(bytes);
    }
    if (typeof Buffer !== 'undefined') {
        return Buffer.from(b64, 'base64').toString('utf8');
    }
    return '';
};
export const extractTextFromSvgDataUrl = (dataUrl, watermark) => {
    try {
        let svgXml = '';
        const base64Prefix = 'data:image/svg+xml;base64,';
        const utf8PrefixA = 'data:image/svg+xml;utf8,';
        const utf8PrefixB = 'data:image/svg+xml,';
        if (!dataUrl) {
            return undefined;
        }
        if (dataUrl.startsWith(base64Prefix)) {
            const b64 = dataUrl.slice(base64Prefix.length);
            svgXml = base64ToUtf8(b64);
            if (!svgXml) {
                return undefined;
            }
        }
        else if (dataUrl.startsWith(utf8PrefixA)) {
            svgXml = decodeURIComponent(dataUrl.slice(utf8PrefixA.length));
        }
        else if (dataUrl.startsWith(utf8PrefixB)) {
            svgXml = decodeURIComponent(dataUrl.slice(utf8PrefixB.length));
        }
        else {
            const input = dataUrl.trim();
            if (input.includes('<svg')) {
                svgXml = input;
            }
            else {
                let decoded;
                try {
                    decoded = base64ToUtf8(input);
                }
                catch {
                    decoded = undefined;
                }
                if (decoded?.includes('<svg')) {
                    svgXml = decoded;
                }
                else {
                    try {
                        const maybeXml = decodeURIComponent(input);
                        if (maybeXml.includes('<svg')) {
                            svgXml = maybeXml;
                        }
                        else {
                            return undefined;
                        }
                    }
                    catch {
                        return undefined;
                    }
                }
            }
        }
        const doc = new DOMParser().parseFromString(svgXml, 'image/svg+xml');
        const texts = Array.from(doc.querySelectorAll('text'));
        const candidates = texts.flatMap((el) => {
            const text = el.textContent ?? '';
            const size = Number(el.getAttribute('font-size')) || 0;
            const family = (el.getAttribute('font-family') || '').toLowerCase();
            return text.replace(/\s/g, '').length > 0 &&
                (!watermark || text.trim() !== watermark)
                ? [{ el, text, size, family }]
                : [];
        });
        if (candidates.length === 0) {
            return undefined;
        }
        const pacifico = candidates.find((c) => c.family.includes('pacifico'));
        if (pacifico) {
            return pacifico.text;
        }
        candidates.sort((a, b) => b.size - a.size);
        return candidates[0].text || undefined;
    }
    catch {
        return undefined;
    }
};
